// 微信 JSSDK 调起「确认收款」页工具（用户确认收款模式）。
// 仅能在微信内置浏览器中生效；外部浏览器 wx.config 会失败，调用方据此提示「请在微信中打开」。

interface WxJsConfig {
  appId: string;
  timestamp: string;
  nonceStr: string;
  signature: string;
}

// 动态加载 jweixin SDK（已加载则直接返回）
export function loadJweixin(): Promise<any> {
  return new Promise((resolve, reject) => {
    const w = window as any;
    if (w.wx && w.wx.config) return resolve(w.wx);
    const script = document.createElement('script');
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
    script.onload = () => (w.wx ? resolve(w.wx) : reject(new Error('微信 JSSDK 加载失败')));
    script.onerror = () => reject(new Error('微信 JSSDK 加载失败，请检查网络'));
    document.head.appendChild(script);
  });
}

// 是否微信内置浏览器（jweixin 仅在此环境可用）
export function isWechatBrowser(): boolean {
  return /MicroMessenger/i.test(navigator.userAgent || '');
}

// 微信内 JSAPI 支付：WeixinJSBridge.invoke('getBrandWCPayRequest')。
// 背景 NATIVE 二维码端内两路全被封（weixin:// 深链被 webview 拦截、长按识别被风控），
// 端内唯一支付方式即 JSAPI —— payParams 由后端下单返回（appId/timeStamp/nonceStr/package/signType/paySign）。
// 无需 wx.config 签名（getBrandWCPayRequest 走原生 JSBridge，非 JSSDK 接口）。
export function requestWechatPayment(payParams: {
  appId: string; timeStamp: string; nonceStr: string; package: string; signType: string; paySign: string;
}): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (!isWechatBrowser()) {
      reject(new Error('请在微信中打开本页完成支付'));
      return;
    }
    let settled = false;
    const finish = (fn: () => void) => { if (!settled) { settled = true; fn(); } };
    const invoke = () => {
      const bridge = (window as any).WeixinJSBridge;
      if (!bridge || typeof bridge.invoke !== 'function') {
        finish(() => reject(new Error('微信支付组件未就绪，请完全退出微信后重新进入本页重试')));
        return;
      }
      bridge.invoke('getBrandWCPayRequest', payParams, (r: any) => {
        const msg = (r && r.err_msg) || '';
        if (msg === 'get_brand_wcpay_request:ok') {
          finish(() => resolve(true)); // ok=支付请求受理成功，实际到账以后端查单/回调为准
        } else if (msg === 'get_brand_wcpay_request:cancel') {
          finish(() => reject(new Error('已取消支付，可点「重新拉起微信支付」继续')));
        } else if (msg.indexOf('fail') >= 0) {
          finish(() => reject(new Error('微信支付失败：' + (msg || '未知错误') + '（若反复出现请联系管理员）')));
        } else {
          finish(() => reject(new Error('微信支付未完成：' + (msg || '未知错误'))));
        }
      });
    };
    // WeixinJSBridge 在页面加载早期可能尚未注入，监听 ready 事件兜底
    const bridge = (window as any).WeixinJSBridge;
    if (bridge && typeof bridge.invoke === 'function') {
      invoke();
    } else {
      document.addEventListener('WeixinJSBridgeReady', invoke, false);
      setTimeout(() => {
        if (!settled) {
          document.removeEventListener('WeixinJSBridgeReady', invoke as any);
          finish(() => reject(new Error('微信支付组件加载超时，请下拉刷新本页重试')));
        }
      }, 5000);
    }
  });
}

// wx.config + 调起确认收款页。成功/失败通过 Promise 返回。
// 2026-09-29 实证三处坑：① wx.ready 后无超时，微信对异常单据既不弹页也不回调 fail → 挂死；
// ② wx.requestMerchantTransfer 仅小程序可用，微信内 H5 的 jweixin 上不存在——typeof 预检
//    必失败 → 误报「微信版本过低」（用户微信 8.0.79 最新版也中招）；
// ③ H5 官方正确姿势（pay.weixin.qq.com/doc/v3/partner/4016960577）：
//    wx.checkJsApi 探测支持性 → WeixinJSBridge.invoke('requestMerchantTransfer', {mchId, appId, package})。
// 对策：全程 20s 总超时 + checkJsApi 官方探测 + WeixinJSBridge 调起。
export function confirmWechatTransfer(
  packageInfo: string,
  mchId: string,
  jsConfig: WxJsConfig
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    if (!isWechatBrowser()) {
      reject(new Error('请在微信中打开本页完成确认收款（当前浏览器不支持）'));
      return;
    }
    if (!packageInfo) {
      reject(new Error('未取到转账单据凭据（package_info），无法拉起确认页'));
      return;
    }
    if (!mchId) {
      reject(new Error('未取到商户号（mchId），无法拉起确认页'));
      return;
    }
    let settled = false;
    let overall: ReturnType<typeof setTimeout>;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(overall);
      fn();
    };
    // 全程总超时：覆盖 config/ready/调起三阶段，任何静默挂死都转成明确报错
    overall = setTimeout(() => {
      finish(() => reject(new Error('调起确认收款超时：微信未响应，请稍后重试；若反复出现请联系管理员核对单据状态')));
    }, 20000);
    loadJweixin()
      .then((wx) => {
        if (!wx || !wx.config) {
          finish(() => reject(new Error('当前环境不支持微信确认收款，请在微信中打开本页')));
          return;
        }
        wx.config({
          debug: false,
          appId: jsConfig.appId,
          timestamp: jsConfig.timestamp,
          nonceStr: jsConfig.nonceStr,
          signature: jsConfig.signature,
          jsApiList: ['requestMerchantTransfer'],
        });
        wx.ready(() => {
          // 官方 H5 示例：checkJsApi 探测支持性（探测不到才是真的版本过低）
          wx.checkJsApi({
            jsApiList: ['requestMerchantTransfer'],
            success: (res: any) => {
              if (!res || !res.checkResult || !res.checkResult['requestMerchantTransfer']) {
                finish(() => reject(new Error('当前微信版本过低，不支持确认收款，请更新微信后重试')));
                return;
              }
              // H5 调起必须走 WeixinJSBridge（wx.requestMerchantTransfer 仅小程序存在）
              const bridge = (window as any).WeixinJSBridge;
              if (!bridge || typeof bridge.invoke !== 'function') {
                finish(() => reject(new Error('微信支付组件未就绪，请完全退出微信后重新进入本页重试')));
                return;
              }
              bridge.invoke(
                'requestMerchantTransfer',
                { mchId: mchId, appId: jsConfig.appId, package: packageInfo },
                (r: any) => {
                  const msg = (r && r.err_msg) || '';
                  if (msg === 'requestMerchantTransfer:ok') {
                    // ok = 确认页展示成功（不代表付款完成，实际到账以微信页面确认为准）
                    finish(() => resolve(true));
                  } else if (msg === 'requestMerchantTransfer:cancel') {
                    finish(() => reject(new Error('已取消收款确认，如需收款请重新点「确认收款」')));
                  } else {
                    finish(() => reject(new Error('微信确认收款失败：' + (msg || '未知错误'))));
                  }
                }
              );
            },
            fail: (err: any) =>
              finish(() => reject(new Error('微信能力检测失败：' + JSON.stringify(err || {})))),
          });
        });
        wx.error((err: any) => finish(() => reject(new Error('微信 JSSDK 配置失败：' + err))));
      })
      .catch((e) => finish(() => reject(e)));
  });
}
